import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { X, Download, Loader2 } from "lucide-react";
import { CloseButton } from "../BackgroundSelectionModal/index.styled";
import BackgroundSelectionModal from "../BackgroundSelectionModal";
import DraggableQuestionOverlay, {
  type OverlayPosition,
} from "../DraggableQuestionOverlay";
import QuestionPreviewBox from "../../shared/QuestionPreviewBox";
import VoiceMessageBar from "../VoiceMessageBar";
import { useMediaRecorder } from "../../../hooks/useMediaRecorder";
import { useAudioAnalyser } from "../../../hooks/useAudioAnalyser";
import { useScrollLock } from "../../../hooks/useScrollLock";
import { exportVoiceStory, renderOverlayElementToImage } from "../../../utils/mediaExportUtils";
import { runBackgroundExport } from "../../../utils/backgroundMediaExport";
import { warmupFfmpeg } from "../../../utils/videoTranscodeUtils";
import { BACKGROUNDS } from "../../../constants/backgrounds";
import { showToast } from "../../../utils/toastUtils";
import {
  MediaAnswerModalContainer,
  MediaAnswerModalOverlay,
  VoicePreviewStage,
} from "../shared/MediaAnswerModal.styled";

interface VoiceAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  boxTitle: string;
  questionText: string;
  footerText?: string;
  exportId: string;
  onDownloaded?: () => void;
}

const ERROR_MESSAGES = {
  permission_denied: "נדרשת הרשאת מיקרופון כדי להקליט",
  not_supported: "הדפדפן לא תומך בהקלטת קול",
  unknown: "שגיאה בהקלטה, נסו שוב",
};

const VoiceAnswerModal = ({
  isOpen,
  onClose,
  boxTitle,
  questionText,
  footerText,
  exportId,
  onDownloaded,
}: VoiceAnswerModalProps) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const exportOverlayRef = useRef<HTMLDivElement>(null);
  const answerOverlayRef = useRef<HTMLDivElement>(null);
  const voiceBarRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playbackSeconds, setPlaybackSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUNDS[0]);
  const [answerPosition, setAnswerPosition] = useState<OverlayPosition>({
    x: 16,
    y: 48,
  });
  const [voiceBarPosition, setVoiceBarPosition] = useState<OverlayPosition>({
    x: 12,
    y: 500,
  });
  const [voiceBarWidth, setVoiceBarWidth] = useState(0);

  useScrollLock(isOpen);

  const {
    isRecording,
    recordedBlob,
    error: recorderError,
    startAudioRecording,
    stopRecording,
    reset,
    stopTracks,
  } = useMediaRecorder();

  const { levels, start, startFromElement, pauseLoop, stop: stopAnalyser } =
    useAudioAnalyser();

  const pausePlayback = useCallback(() => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    audioRef.current?.pause();
    setIsPlaying(false);
    pauseLoop();
    if (audioRef.current) {
      setPlaybackSeconds(Math.floor(audioRef.current.currentTime));
    }
  }, [pauseLoop]);

  const resetPlayback = useCallback(() => {
    pausePlayback();
    if (audioRef.current) audioRef.current.currentTime = 0;
    stopAnalyser();
    setPlaybackSeconds(0);
  }, [pausePlayback, stopAnalyser]);

  const resetLayout = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const stageW = stage.clientWidth;
    const stageH = stage.clientHeight;
    const barW = Math.round(stageW * 0.93);
    const barH = voiceBarRef.current?.offsetHeight ?? 52;

    setVoiceBarWidth(barW);
    setAnswerPosition({
      x: 0,
      y: Math.round(stageH * 0.12),
    });
    setVoiceBarPosition({
      x: 0,
      y: Math.round(stageH - barH - stageH * 0.04),
    });
  }, []);

  const syncLayoutOnResize = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const stageW = stage.clientWidth;
    const stageH = stage.clientHeight;
    const barW = Math.round(stageW * 0.93);
    const barH = voiceBarRef.current?.offsetHeight ?? 52;
    const answerH = answerOverlayRef.current?.offsetHeight ?? 0;
    const voiceBarBottomInset = stageH * 0.04;
    const maxAnswerY = Math.max(0, stageH - answerH);
    const maxVoiceBarY = Math.max(0, stageH - barH - voiceBarBottomInset);

    setVoiceBarWidth(barW);
    setAnswerPosition((p) => ({
      ...p,
      y: Math.min(maxAnswerY, Math.max(0, p.y)),
    }));
    setVoiceBarPosition((p) => ({
      ...p,
      y: Math.min(maxVoiceBarY, Math.max(0, p.y)),
    }));
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    stopRecording();
    stopTracks();
    resetPlayback();
    clearTimer();
    reset();
    setElapsedSeconds(0);
    setRecordingDuration(0);
    setPlaybackSeconds(0);
    setIsBackgroundModalOpen(false);
    setSelectedBackground(BACKGROUNDS[0]);
    onClose();
  }, [
    clearTimer,
    onClose,
    reset,
    resetPlayback,
    stopRecording,
    stopTracks,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    warmupFfmpeg();

    reset();
    setElapsedSeconds(0);
    setRecordingDuration(0);
    setPlaybackSeconds(0);
    setIsPlaying(false);
    setSelectedBackground(BACKGROUNDS[0]);

    return () => {
      stopTracks();
      resetPlayback();
      clearTimer();
    };
  }, [isOpen, reset, stopTracks, resetPlayback, clearTimer]);

  useLayoutEffect(() => {
    if (!isOpen || !stageRef.current) return;

    const stage = stageRef.current;
    resetLayout();
    const observer = new ResizeObserver(() => syncLayoutOnResize());
    observer.observe(stage);

    return () => observer.disconnect();
  }, [isOpen, resetLayout, syncLayoutOnResize]);

  useEffect(() => {
    if (!recordedBlob) {
      audioRef.current = null;
      return;
    }

    const url = URL.createObjectURL(recordedBlob);
    const audio = new Audio(url);
    audioRef.current = audio;

    const handleEnded = () => {
      pausePlayback();
      setPlaybackSeconds(recordingDuration);
    };

    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      URL.revokeObjectURL(url);
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [recordedBlob, recordingDuration, pausePlayback]);

  useEffect(() => {
    if (isRecording) return;
    if (!recordedBlob) return;
    setRecordingDuration(elapsedSeconds);
    setPlaybackSeconds(elapsedSeconds);
  }, [isRecording, recordedBlob, elapsedSeconds]);

  useEffect(() => {
    if (isRecording) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      clearTimer();
    }

    return clearTimer;
  }, [isRecording, clearTimer]);

  const handleToggleRecord = async () => {
    if (isRecording) {
      stopRecording();
      stopAnalyser();
      return;
    }

    resetPlayback();
    const stream = await startAudioRecording();
    if (stream) start(stream);
  };

  const handleTogglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !recordedBlob) return;

    if (isPlaying) {
      pausePlayback();
      return;
    }

    try {
      startFromElement(audio);
      await audio.play();
      setIsPlaying(true);
      playbackTimerRef.current = setInterval(() => {
        setPlaybackSeconds(Math.floor(audio.currentTime));
      }, 200);
    } catch (e) {
      console.error(e);
      showToast("לא ניתן להשמיע את ההקלטה");
    }
  };

  const handleRetake = () => {
    resetPlayback();
    reset();
    setElapsedSeconds(0);
    setRecordingDuration(0);
    setPlaybackSeconds(0);
  };

  const handleDownload = async () => {
    if (!recordedBlob || !exportOverlayRef.current || !stageRef.current) return;
    if (recordedBlob.size === 0) {
      showToast("ההקלטה ריקה, נסו להקליט שוב");
      return;
    }

    const stage = stageRef.current;
    const cardWidth =
      answerOverlayRef.current?.offsetWidth ?? Math.max(1, stage.clientWidth - 12);
    exportOverlayRef.current.style.width = `${cardWidth}px`;

    const voiceBarEl = voiceBarRef.current;
    const voiceBarSize = voiceBarEl
      ? {
          width: voiceBarEl.offsetWidth,
          height: voiceBarEl.offsetHeight,
        }
      : undefined;

    setIsPreparing(true);
    try {
      const overlayImg = await renderOverlayElementToImage(exportOverlayRef.current);
      const exportBlob = new Blob([await recordedBlob.arrayBuffer()], {
        type: recordedBlob.type,
      });

      await runBackgroundExport(
        () =>
          exportVoiceStory({
            audioBlob: exportBlob,
            overlayImg,
            background: selectedBackground,
            filename: `voice-answer-${exportId}`,
            overlayPosition: answerPosition,
            voiceBarPosition,
            voiceBarSize,
            previewWidth: stage.clientWidth,
            previewHeight: stage.clientHeight,
          }),
        {
          success: "הסרטון הורד בהצלחה!",
          error: "שגיאה בהורדת ההודעה, נסו שוב",
        },
      );
      onDownloaded?.();
    } catch (e) {
      console.error(e);
      showToast("שגיאה בהורדת ההודעה, נסו שוב", "error");
    } finally {
      setIsPreparing(false);
    }
  };

  if (!isOpen) return null;

  const overlayContent = (
    <QuestionPreviewBox
      title={boxTitle}
      footerText={footerText?.trim() ? footerText : undefined}
    >
      <span style={{ whiteSpace: "pre-wrap" }}>{questionText}</span>
    </QuestionPreviewBox>
  );

  const hasPostRecordingActions = !!recordedBlob && !isRecording;
  const displaySeconds =
    hasPostRecordingActions && !isRecording
      ? isPlaying
        ? playbackSeconds
        : recordingDuration || playbackSeconds
      : elapsedSeconds;

  const modal = (
    <MediaAnswerModalOverlay onClick={handleClose}>
      <MediaAnswerModalContainer
        role="dialog"
        aria-modal="true"
        $compactPreview={hasPostRecordingActions}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={handleClose} aria-label="סגור">
          <X size={17} />
        </CloseButton>

        <h3 className="modal-title">הקלטת קול</h3>

        <VoicePreviewStage
          ref={stageRef}
          className="preview-stage"
          $background={selectedBackground}
        >
          <DraggableQuestionOverlay
            position={answerPosition}
            onPositionChange={setAnswerPosition}
            containerRef={stageRef}
            disabled={isRecording}
            axis="vertical"
          >
            <div ref={answerOverlayRef} style={{ width: "100%" }}>
              {overlayContent}
            </div>
          </DraggableQuestionOverlay>

          <DraggableQuestionOverlay
            position={voiceBarPosition}
            onPositionChange={setVoiceBarPosition}
            containerRef={stageRef}
            disabled={isRecording}
            width={voiceBarWidth || undefined}
            zIndex={3}
            axis="vertical"
            bottomInsetRatio={0.04}
          >
            <div ref={voiceBarRef}>
              <VoiceMessageBar
                levels={levels}
                isRecording={isRecording}
                isPlaying={isPlaying}
                elapsedSeconds={displaySeconds}
                onToggleRecord={handleToggleRecord}
                onTogglePlayback={handleTogglePlayback}
                hasRecording={!!recordedBlob}
              />
            </div>
          </DraggableQuestionOverlay>
        </VoicePreviewStage>

        {recorderError && (
          <p className="error-message">{ERROR_MESSAGES[recorderError]}</p>
        )}

        <div className={`actions-row${hasPostRecordingActions ? " post-recording" : ""}`}>
          {isRecording ? (
            <button
              type="button"
              className="action-btn danger"
              onClick={handleToggleRecord}
            >
              עצור הקלטה
            </button>
          ) : recordedBlob ? (
            <>
              <div className="secondary-pair">
                <button
                  type="button"
                  className="action-btn secondary"
                  onClick={() => setIsBackgroundModalOpen(true)}
                >
                  בחירת רקע
                </button>
                <button
                  type="button"
                  className="action-btn secondary"
                  onClick={handleRetake}
                >
                  הקלט שוב
                </button>
              </div>
              <button
                type="button"
                className="action-btn"
                onClick={handleDownload}
                disabled={isPreparing}
              >
                {isPreparing ? (
                  <Loader2 size={14} className="loading-icon" />
                ) : (
                  <Download size={14} />
                )}
                הורדה
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="action-btn secondary"
                onClick={() => setIsBackgroundModalOpen(true)}
              >
                בחירת רקע
              </button>
              <button
                type="button"
                className="action-btn"
                onClick={handleToggleRecord}
              >
                התחל הקלטה
              </button>
            </>
          )}
        </div>

        <div ref={exportOverlayRef} className="hidden-export-overlay">
          {overlayContent}
        </div>
      </MediaAnswerModalContainer>

      <BackgroundSelectionModal
        isOpen={isBackgroundModalOpen}
        onClose={() => setIsBackgroundModalOpen(false)}
        mode="select"
        title="בחירת רקע"
        initialBackground={selectedBackground}
        onBackgroundSelected={setSelectedBackground}
      />
    </MediaAnswerModalOverlay>
  );

  return createPortal(modal, document.body);
};

export default VoiceAnswerModal;
